import {
  Component,
  computed,
  effect,
  inject,
  Input,
  input,
  output,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  HsButtonModule,
  HsCardModule,
  HsIconModule,
  HsInputModule,
  HsMenuModule,
} from '@hsetu/ngui-components';
import { ICON_PLUS } from '@shared/ui/icons/icon-constants';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Contact } from '@modules/contact/models/contact.model';
import {
  HsContact,
  HsContactData,
  HsContactDetailsComponent,
  HsContactDetailsConfig,
  HsContactModule,
} from '@hsetu/ngui-views';
import { ContactMapper } from '@modules/contact/mappers/contact.mapper';
import { HsLayerModule } from '@hsetu/ngui-resources';
import { Dialog, DialogModule } from '@angular/cdk/dialog';
import { KfmContactBookDialogComponent } from '../contact-book-dialog/kfm-contact-book-dialog.component';
import { ContactService } from '@modules/contact/services/contact.service';
import { KfmContactCreateEditDialogComponent } from '../contact-create-edit-dialog/kfm-contact-create-edit-dialog.component';
import {
  ADD_NEW_ADDRESS_LABEL,
  FROM_CONTACT_BOOK_LABEL,
  NEW_ADDRESS_LABEL,
} from '@modules/contact/i18n/label-text-constants';

@Component({
  selector: 'kfm-contact-card',
  imports: [
    CommonModule,
    HsCardModule,
    HsInputModule,
    HsContactDetailsComponent,
    HsIconModule,
    HsButtonModule,
    HsLayerModule,
    HsMenuModule,
    DialogModule,
    HsContactModule,
  ],
  templateUrl: './kfm-contact-card.component.html',
})
export class KfmContactCardComponent {
  private readonly _sanitizer = inject(DomSanitizer);
  private readonly _contactService = inject(ContactService);
  private readonly _mapper = new ContactMapper();
  private readonly _dialog = inject(Dialog);

  private isContactBookOpen = false;

  private internalContact = signal<HsContact | undefined>(undefined);

  protected readonly iconNew =
    this._sanitizer.bypassSecurityTrustHtml(ICON_PLUS);

  protected readonly addNewContactLabel = ADD_NEW_ADDRESS_LABEL;
  protected readonly addFromAddressBookLabel = FROM_CONTACT_BOOK_LABEL;
  protected readonly newContactLabel = NEW_ADDRESS_LABEL;

  protected readonly config: HsContactDetailsConfig;

  protected readonly hsContact = computed(() => {
    const internalContact = this.internalContact();
    return internalContact;
  });

  @Input()
  public set contact(value: Contact | undefined) {
    if (value) {
      const hsContact = this._mapper.mapToHsContact(value);
      this.internalContact.set(hsContact);
    } else {
      this.internalContact.set(undefined);
    }
  }

  public cardTitle = input<string>('');
  public cardIcon = input<SafeHtml | undefined>(undefined);
  public isEditable = input<boolean>(true);

  public contactAdded = output<Contact>();
  public contactRemoved = output<void>();

  constructor() {
    this.config = new HsContactDetailsConfig();
    this.config.showContactOpenBtn.set(false);
    this.config.btnClicked$.subscribe((value) => {
      if (value[1] === 7) {
        // Edit contact
        this.onEditContact(value[0]);
      } else if (value[1] === 8) {
        // Delete contact
        this.contactRemoved.emit();
      }
    });

    effect(() => {
      const editable = this.isEditable();
      this.config.showDeleteBtn.set(editable);
      this.config.showEditBtn.set(editable);
    });
  }

  private onEditContact(hsContact: HsContact) {
    if (this.isContactBookOpen) return;
    this.isContactBookOpen = true;

    this._contactService.getContactById(hsContact.uId).then((contact) => {
      const mappedContact = contact
        ? this._mapper.mapToHsContact(contact)
        : hsContact;

      const dialogRef = this._dialog.open<Contact>(
        KfmContactCreateEditDialogComponent,
        {
          width: '600px',
          maxWidth: '90vw',
          maxHeight: '90vh',
          data: {
            contact: mappedContact,
            bankAccount: contact?.bankAccounts?.[0] ?? null,
            registrations: contact?.registrations ?? null,
          },
        },
      );

      dialogRef.closed.subscribe(async (result) => {
        if (result) {
          const ok = await this._contactService.update(result);
          if (ok) {
            this.internalContact.set(this._mapper.mapToHsContact(result));
            this.contactAdded.emit(result);
          }
        }
        this.isContactBookOpen = false;
      });
    });
  }

  protected onAddNewContact() {
    if (this.isContactBookOpen) {
      return;
    }

    this.isContactBookOpen = true;

    const dialogRef = this._dialog.open<Contact>(
      KfmContactCreateEditDialogComponent,
      {
        width: '600px',
        maxWidth: '90vw',
        maxHeight: '90vh',
        data: null,
      },
    );

    dialogRef.closed.subscribe(async (result) => {
      if (result) {
        // Create new contact
        const contact = await this._contactService.create(result, 1);

        if (!contact) {
          this.isContactBookOpen = false;
          return;
        }

        // Get the contact from service
        const newContact = await this._contactService.getContactById(contact.uId);

        if (newContact) {
          this.internalContact.set(this._mapper.mapToHsContact(newContact));
          this.contactAdded.emit(newContact);
        }
      }

      this.isContactBookOpen = false;
    });
  }

  protected onAddFromAddressBook() {
    if (this.isContactBookOpen) {
      return;
    }

    this.isContactBookOpen = true;

    const dialogRef = this._dialog.open<HsContactData>(
      KfmContactBookDialogComponent,
      {
        width: '600px',
        maxWidth: '90vw',
        maxHeight: '90vh',
      },
    );

    dialogRef.closed.subscribe(async (result) => {
      if (result) {
        this.internalContact.set(result as HsContact);

        // Get the contact from service
        const contact = await this._contactService.getContactById(result.uId);

        if (contact) {
          this.contactAdded.emit(contact);
        }
      }

      this.isContactBookOpen = false;
    });
  }
}
